import { QueryAggregator } from "@/api/utils/QueryAggregator"
import { useCurrentAccountStore } from "@/stores/currentAccount"
import { AccountService } from "@/services/AccountService"
import { PaymentScheduleService as ScheduleService } from "@/services/PaymentScheduleService"
import { getSuggestedPaymentBreakdown } from "./getSuggestedPayment"
import { getAccountMeterpointAgreements } from "./getAccountMeterpointAgreements"
import { getActiveMeterpointAgreements } from "./getActiveMeterpointAgreements"
import {
  SEASONAL_FACTOR_SUMMER,
  SEASONAL_FACTOR_WINTER,
  WINTER_MONTHS,
} from "@/constants/finance"
import dayjs, { Dayjs } from "dayjs"
import isBetween from "dayjs/plugin/isBetween"
import isSameOrAfter from "dayjs/plugin/isSameOrAfter"
import isSameOrBefore from "dayjs/plugin/isSameOrBefore"

dayjs.extend(isBetween)
dayjs.extend(isSameOrAfter)
dayjs.extend(isSameOrBefore)

export interface IPredictedPayment {
  balance: number
  fromDate: Dayjs
}

export class PredictedPayment implements IPredictedPayment {
  balance: number
  fromDate: Dayjs

  constructor(balance: number, fromDate: Dayjs) {
    this.balance = Number(balance.toFixed(2))
    this.fromDate = fromDate
  }
}

const MAX_DAYJS = dayjs("9999-12-31")
const UNIT_FACTOR = 100

const isWinter = (date: Dayjs) => {
  return WINTER_MONTHS.includes(date.month() + 1)
}

/**
 * @param {boolean} useSuggestedPaymentBias Use suggested payment amounts.
 * @param {number} quantity The number of estimations to return.
 * @returns An array of payment estimations for predicted billing periods.
 */

export const getPredictedPayments = (
  quantity = 10,
  useSuggestedPaymentBias = false,
) => {
  const queries = new QueryAggregator()
  const { accountNumber, transactions, meterpoints } = useCurrentAccountStore()

  const historicScheduleQuery =
    ScheduleService.getHistoricSchedules(accountNumber)
  const currentScheduleQuery = ScheduleService.getCurrentSchedule(accountNumber)
  const futureSchedulesQuery = ScheduleService.getFutureSchedules(accountNumber)
  const accountQuery = AccountService.getAccount(accountNumber)
  const suggestedAmountQuery = getSuggestedPaymentBreakdown(
    accountNumber,
    true,
    true,
  )
  const agreementsQuery = getAccountMeterpointAgreements(accountNumber)
  const activeMeterpointAgreementsQuery =
    getActiveMeterpointAgreements(accountNumber)

  queries.add(transactions)
  queries.add(meterpoints)
  queries.add(historicScheduleQuery)
  queries.add(currentScheduleQuery)
  queries.add(futureSchedulesQuery)
  queries.add(accountQuery)
  queries.add(suggestedAmountQuery)
  queries.add(agreementsQuery)
  queries.add(activeMeterpointAgreementsQuery)

  let predictedPayments = [] as IPredictedPayment[]

  if (!queries.getAggregate().isFetched || !accountQuery.data.isActive)
    return queries.setData(predictedPayments).getAggregate()

  const scheduledPaymentDate =
    currentScheduleQuery.data?.nextPaymentDt ||
    historicScheduleQuery.data[0]?.nextPaymentDt ||
    dayjs()

  const lastPaymentDate = transactions.data
    .filter((transaction) => transaction.isAccepted)
    .sort((a, b) => (a.acceptedDttm?.isAfter(b.acceptedDttm) ? 1 : -1))
    .at(-1)?.acceptedDttm

  const initialDate =
    lastPaymentDate ??
    currentScheduleQuery.data?.fromDt ??
    historicScheduleQuery.data[0]?.fromDt ??
    dayjs()

  const futureScheduledPaymentDates = futureSchedulesQuery.data.map(
    (schedule) => schedule.nextPaymentDt,
  )

  const predictedPaymentDates = Array.from({ length: quantity - 1 }).reduce(
    (acc: Dayjs[]) => {
      const nextScheduledPaymentDate = acc[acc.length - 1].add(1, "month")
      const futureSchedule = futureScheduledPaymentDates.find((date) =>
        nextScheduledPaymentDate.isAfter(date),
      )

      return futureSchedule
        ? [...acc, futureSchedule]
        : [...acc, nextScheduledPaymentDate]
    },
    [scheduledPaymentDate],
  )

  const initialData = [
    ...[new PredictedPayment(-accountQuery.data.balance, initialDate)],
    ...predictedPaymentDates.map((date) => {
      return new PredictedPayment(-accountQuery.data.balance, date)
    }),
  ]

  const getDirectDebitCostForInterval = (
    fromDt: Dayjs,
    useSuggestedPayments = false,
  ) => {
    const futureSchedule = futureSchedulesQuery.data.find(
      (schedule) =>
        fromDt.isAfter(schedule.fromDt) &&
        (schedule?.toDt ? schedule?.toDt?.isAfter(fromDt) : true),
    )

    const schedule = futureSchedule ?? currentScheduleQuery.data
    const isSeasonal = schedule?.isSeasonal

    const amount = useSuggestedPayments
      ? suggestedAmountQuery?.data?.recommendedMonthlyPayment || 0
      : schedule?.amount || 0

    if (isSeasonal) {
      return isWinter(fromDt)
        ? amount * SEASONAL_FACTOR_WINTER
        : amount * SEASONAL_FACTOR_SUMMER
    } else {
      return amount
    }
  }

  const calculateUsageCost = (
    meterpointIdentifier: string,
    agreementId: number,
    days: number,
    useWinterBias: boolean,
  ) => {
    const meterpoint = meterpoints.data.find(
      (meterpoint) => meterpoint.identifier === meterpointIdentifier,
    )
    const tariff = activeMeterpointAgreementsQuery.data.find(
      (agreement) => agreement.id === agreementId,
    )?.tariff

    if (!tariff?.productDetails) return 0

    const alp =
      (1 / 12) *
      (useWinterBias ? SEASONAL_FACTOR_WINTER : SEASONAL_FACTOR_SUMMER)

    const consumptionCost =
      meterpoint?.registers.reduce((acc, register) => {
        if (tariff.isGas && tariff.productDetails.unitRate) {
          return (
            acc +
            (tariff.productDetails.unitRate * register.usage * alp) /
              UNIT_FACTOR
          )
        }

        if (tariff.productDetails?.unitRates) {
          const unitRate =
            tariff.productDetails.unitRates[register.rateName ?? "Standard"] ||
            tariff.productDetails.unitRates.Standard
          if (unitRate) {
            return acc + (unitRate * register.usage * alp) / UNIT_FACTOR
          }
        }

        return acc
      }, 0) ?? 0

    const standingChargeCost =
      (tariff.productDetails.standingCharge * days) / UNIT_FACTOR
    return standingChargeCost + consumptionCost
  }

  const getEstimatedUsageCostForMeterpoints = (
    startDt: Dayjs,
    endDt: Dayjs,
  ) => {
    let totalUsageCost = 0

    for (const meterpointIdentifier in agreementsQuery.data) {
      totalUsageCost += agreementsQuery.data[meterpointIdentifier].reduce(
        (acc, agreement) => {
          const agreementEndDt = agreement.endDate ?? MAX_DAYJS
          if (
            (agreement.startDate.isSameOrAfter(startDt) &&
              agreementEndDt.isSameOrBefore(endDt)) ||
            startDt.isBetween(agreement.startDate, agreementEndDt) ||
            endDt.isBetween(agreement.startDate, agreementEndDt)
          ) {
            const diffEndDate = endDt.isAfter(agreement.endDate)
              ? (agreement.endDate ?? MAX_DAYJS)
              : endDt
            const durationOfDays = diffEndDate.diff(startDt, "day")
            return (
              acc +
              calculateUsageCost(
                meterpointIdentifier,
                agreement.id,
                durationOfDays,
                isWinter(startDt),
              )
            )
          }
          return acc
        },
        0,
      )
    }

    return totalUsageCost
  }

  predictedPayments = initialData
    .reduce(
      (acc: IPredictedPayment[], curr: IPredictedPayment, index) => {
        const prev = acc[acc.length - 1]
        const directDebit = getDirectDebitCostForInterval(
          prev.fromDate,
          useSuggestedPaymentBias,
        )
        const estimatedUsageCost = getEstimatedUsageCostForMeterpoints(
          prev.fromDate,
          curr.fromDate,
        )

        return [
          ...acc,
          new PredictedPayment(
            index !== 0
              ? prev.balance + directDebit - estimatedUsageCost
              : curr.balance,
            curr.fromDate,
          ),
        ]
      },
      [initialData[0]],
    )
    .slice(1)

  return queries.setData(predictedPayments).getAggregate()
}
